import * as React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { BREAKPOINTS } from '@/constants';
import { DEMO_COLORS_DARK } from '@/constants/Wham.colors';

import Space from '@/components/Space';
import ToggleButtonGroup from '@/components/SleekToggleButtonGroup';

import KeyframeGraph from './KeyframeGraph';

interface Props {}

type Mode = 'with-from' | 'without-from';

const GRAPH_WIDTH = 500;
const GRAPH_HEIGHT = 150;
const KEYFRAME_START = 0.3;
const KEYFRAME_END = 0.8;

function WithAndWithoutFromGraph({}: Props) {
  const [mode, setMode] = React.useState<Mode>('with-from');

  const paths = calculatePaths(mode);

  return (
    <Wrapper>
      <ToggleButtonGroup
        options={[
          {
            label: 'Full',
            value: 'with-from',
          },
          {
            label: 'Partial',
            value: 'without-from',
          },
        ]}
        value={mode}
        handleSelect={(value) => {
          setMode(value as Mode);
        }}
      />
      <StyledKeyframeGraph>
        <FlagWrapper>
          <Flag
            width="12"
            height="188"
            viewBox="0 0 12 188"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2 186V8.5C2 4.63401 5.13401 1.5 9 1.5H10"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="6 6"
            />
          </Flag>
          <FlagLabel>Keyframe Start</FlagLabel>
        </FlagWrapper>

        <Svg
          viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          {paths.map((path, i) => (
            <AnimatedLine key={i} path={path} />
          ))}
        </Svg>
      </StyledKeyframeGraph>
    </Wrapper>
  );
}

function AnimatedLine({ path }: { path: string }) {
  const spring = useSpring({
    d: path,
  });

  return <Line d={spring.d} fill="none" />;
}

const STARTING_OPACITIES = [1, 0.6, 0.3];

const BEZIER_OFFSET = 50;

function calculatePaths(mode: Mode) {
  if (mode === 'with-from') {
    return STARTING_OPACITIES.map((s, i) => {
      return `
        M 0 ${s * GRAPH_HEIGHT}
        L ${GRAPH_WIDTH * KEYFRAME_START} ${s * GRAPH_HEIGHT}
        L ${GRAPH_WIDTH * KEYFRAME_START} ${GRAPH_HEIGHT}
        C ${
          GRAPH_WIDTH * KEYFRAME_START + BEZIER_OFFSET
        } ${GRAPH_HEIGHT}
          ${GRAPH_WIDTH * KEYFRAME_END - BEZIER_OFFSET} 0
          ${GRAPH_WIDTH * KEYFRAME_END} 0
        L ${GRAPH_WIDTH - 1.5} 0
      `;
    });
  } else {
    return STARTING_OPACITIES.map((s) => {
      return `
        M 0 ${s * GRAPH_HEIGHT}
        L ${GRAPH_WIDTH * KEYFRAME_START} ${s * GRAPH_HEIGHT}
        L ${GRAPH_WIDTH * KEYFRAME_START} ${s * GRAPH_HEIGHT}
        C ${GRAPH_WIDTH * KEYFRAME_START + BEZIER_OFFSET} ${
          s * GRAPH_HEIGHT
        }
          ${GRAPH_WIDTH * KEYFRAME_END - BEZIER_OFFSET} 0
          ${GRAPH_WIDTH * KEYFRAME_END} 0
        L ${GRAPH_WIDTH - 1.5} 0
      `;
    });
  }
}

const Wrapper = styled(Space)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin-top: 1rem;
  margin-bottom: 4rem;
  margin-inline: -16px;
  padding: 1rem;
  padding-bottom: 4rem;

  @media ${BREAKPOINTS.mdAndLarger} {
    border-radius: 8px;
  }
`;

const StyledKeyframeGraph = styled(KeyframeGraph)`
  margin-top: 4rem;
`;

const Svg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: scaleY(-1);
  overflow: visible;
`;

const Line = styled(animated.path)`
  stroke: hsl(50deg 100% 50%);
  stroke: ${DEMO_COLORS_DARK.primary500};
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-opacity: 1;
  mix-blend-mode: plus-lighter;
`;

const FlagWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: calc(${KEYFRAME_START * 100}% - 2px);
  height: 188px;
`;

const Flag = styled.svg`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 12px;
  height: 188px;
  max-width: revert;
`;

const FlagLabel = styled.div`
  position: absolute;
  top: -0.6rem;
  left: 0.85rem;
  font-size: 0.75rem;
  font-weight: var(--font-weight-normal);
  white-space: nowrap;
  font-family: var(--font-family-mono);
`;

export default WithAndWithoutFromGraph;
